import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Brain, Heart, Wine, Shield, Clock, Search, ArrowRight } from "lucide-react";

const CONDITIONS_LIST = [
  { name: "Depression", slug: "depression", category: "Mood Disorders", icon: Heart },
  { name: "Anxiety Disorders", slug: "anxiety", category: "Anxiety Disorders", icon: Shield },
  { name: "OCD", slug: "ocd", category: "OCD & Related", icon: Shield },
  { name: "ADHD", slug: "adhd", category: "Child & Adolescent", icon: Brain },
  { name: "Bipolar Disorder", slug: "bipolar-disorder", category: "Mood Disorders", icon: Heart },
  { name: "Schizophrenia", slug: "schizophrenia", category: "Psychotic Disorders", icon: Brain },
  { name: "Alcohol Addiction", slug: "alcohol-addiction", category: "Drug De-addiction", icon: Wine },
  { name: "Drug Addiction", slug: "drug-addiction", category: "Drug De-addiction", icon: Wine },
  { name: "Erectile Dysfunction", slug: "erectile-dysfunction", category: "Sexual Health", icon: Heart },
  { name: "Premature Ejaculation", slug: "premature-ejaculation", category: "Sexual Health", icon: Heart },
  { name: "Performance Anxiety", slug: "performance-anxiety", category: "Sexual Health", icon: Shield },
  { name: "Insomnia", slug: "insomnia", category: "Sleep Disorders", icon: Clock },
  { name: "PTSD", slug: "ptsd", category: "Trauma & Stress", icon: Shield },
  { name: "Dementia", slug: "dementia", category: "Geriatric Psychiatry", icon: Brain },
  { name: "Panic Disorder", slug: "panic-disorder", category: "Anxiety Disorders", icon: Shield },
  { name: "Child & Adolescent Mental Health", slug: "child-psychiatry", category: "Child & Adolescent", icon: Brain },
];

const CATEGORIES = ["All", ...new Set(CONDITIONS_LIST.map(c => c.category))];

export default function ConditionsPage() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = CONDITIONS_LIST.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === "All" || c.category === activeCategory;
    return matchSearch && matchCat;
  });

  return (
    <div>
      <section className="py-16 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4" data-testid="conditions-heading">
            Conditions We Treat
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Comprehensive, evidence-based treatment for a wide range of mental health conditions. Click any condition to learn about symptoms, causes, and treatment options.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="conditions-search"
                placeholder="Search conditions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                data-testid={`category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  activeCategory === cat
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card text-muted-foreground border-border hover:border-primary/50"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {filtered.map(c => (
              <Link key={c.slug} to={`/conditions/${c.slug}`} data-testid={`condition-link-${c.slug}`}>
                <Card className="h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer border">
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 rounded-lg bg-primary/10 shrink-0">
                        <c.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs text-muted-foreground uppercase tracking-wider">{c.category}</span>
                        <h3 className="font-heading font-semibold text-base mt-1">{c.name}</h3>
                        <span className="text-sm text-primary font-medium inline-flex items-center gap-1 mt-2">
                          Learn more <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground">No conditions match your search. Please try different keywords.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
